import { FC, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "../../../../components/button/Button";
import { Heading } from "../../../../components/heading/Heading";
import { IconLogoSuccessFactoryNoText } from "../../../../components/icon/IconLogoSuccessFactoryNoText";
import { Panel, PanelWidth } from "../../../../components/panel/Panel";
import Price from "../../../../components/price/Price";
import { Modal, ModalKind } from "../../../../components/modal/Modal";
import { showGraphqlValidationErrors } from "../../../../services/showGraphqlValidationErrors";
import {
  PaymentSourceScrCredit,
  PaymentSourceShopCredit,
  useConvertUsdsWToShopCreditMutation,
  useWithdrawUsdsWMutation,
} from "../../../../graphql/schema";
import { PaymentMethodsSource } from "../payment-methods-section/PaymentMethodsSection";

import styles from "./top-up-section.module.scss";

interface TopUpSectionProps {
  topUpRoute: string;
  paymentSources?: PaymentMethodsSource[] | null;
  usdsWBalance?: number | null;
}

export const TopUpSection: FC<TopUpSectionProps> = ({ paymentSources, topUpRoute, usdsWBalance }) => {
  // access translation keys
  const { t } = useTranslation();
  const { push } = useHistory();

  const topUp = getShopCreditInfo(paymentSources);
  const scrInfo = getScrCreditInfo(paymentSources);

  const availableUsdsW = usdsWBalance ?? 0;
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [convertAmount, setConvertAmount] = useState("");
  const [convertError, setConvertError] = useState<string | undefined>();
  const [convertUsdsWToShopCredit, { loading: converting }] = useConvertUsdsWToShopCreditMutation();

  const openConvert = () => {
    setConvertAmount("");
    setConvertError(undefined);
    setIsConvertOpen(true);
  };

  const closeConvert = () => {
    setIsConvertOpen(false);
    setConvertAmount("");
    setConvertError(undefined);
  };

  const handleConvert = async (e: FormEvent) => {
    e.preventDefault();
    const numeric = parseFloat(convertAmount);
    if (!convertAmount || isNaN(numeric) || numeric <= 0) {
      setConvertError(t("Amount must be greater than 0"));
      return;
    }
    if (numeric > availableUsdsW) {
      setConvertError(t("Amount cannot exceed available USDSw"));
      return;
    }
    setConvertError(undefined);

    try {
      const result = await convertUsdsWToShopCredit({
        variables: { amount: convertAmount },
        refetchQueries: ["PaymentMethodsView"],
      });
      if (result.data?.convertUsdsWToShopCredit) {
        toast.success(t("Converted to shop credit"));
        closeConvert();
      } else {
        toast.error(result.errors?.[0]?.message ?? t("Something went wrong"));
      }
    } catch (err: any) {
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err.message);
      }
    }
  };

  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawWallet, setWithdrawWallet] = useState("");
  const [withdrawError, setWithdrawError] = useState<string | undefined>();
  const [withdrawUsdsW, { loading: withdrawing }] = useWithdrawUsdsWMutation();

  const openWithdraw = () => {
    setWithdrawAmount("");
    setWithdrawWallet("");
    setWithdrawError(undefined);
    setIsWithdrawOpen(true);
  };

  const closeWithdraw = () => {
    setIsWithdrawOpen(false);
    setWithdrawAmount("");
    setWithdrawWallet("");
    setWithdrawError(undefined);
  };

  const handleWithdraw = async (e: FormEvent) => {
    e.preventDefault();
    const numeric = parseFloat(withdrawAmount);
    if (!withdrawAmount || isNaN(numeric) || numeric <= 0) {
      setWithdrawError(t("Amount must be greater than 0"));
      return;
    }
    if (numeric > availableUsdsW) {
      setWithdrawError(t("Amount cannot exceed available USDSw"));
      return;
    }
    if (!withdrawWallet.trim()) {
      setWithdrawError(t("Wallet address is required"));
      return;
    }
    setWithdrawError(undefined);

    try {
      const result = await withdrawUsdsW({
        variables: { amount: withdrawAmount, walletAddress: withdrawWallet.trim() },
        refetchQueries: ["PaymentMethodsView"],
      });
      if (result.data?.withdrawUsdsW) {
        toast.success(t("Withdrawal requested"));
        closeWithdraw();
      } else {
        toast.error(result.errors?.[0]?.message ?? t("Something went wrong"));
      }
    } catch (err: any) {
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err.message);
      }
    }
  };

  return (
    <Panel className={styles.wrap} contentClassName={styles["wrap-content"]}>
      <Panel className={styles.panel} contentClassName={styles.content} panelStyle="TERTIARY">
        <IconLogoSuccessFactoryNoText className={styles.logo} width={41} height={50} />
        <span className={styles.credit}>
          {topUp ? (
            <Price price={topUp.available} currency={topUp.currency.code} hideSign />
          ) : (
            // empty placeholder to prevent content 'jumping' when data hasn't loaded
            <div style={{ height: "37px" }} />
          )}
        </span>
        <span className={styles.label}>{t("USDS")}</span>
        <Button
          className={styles.button}
          height="MEDIUM"
          kind="SOLID"
          shape="RECT"
          borderRadius="MEDIUM"
          color="ORANGE"
          weight="BOLD"
          fontSize={16}
          onClick={() => push(topUpRoute)}
        >
          {t("Top up")}
        </Button>
      </Panel>
      {availableUsdsW > 0 && (
        <Panel className={styles.panel} contentClassName={styles["content-small"]} panelStyle="TERTIARY">
          <IconLogoSuccessFactoryNoText className={styles.logo} width={41} height={50} />
          <span className={styles.credit}>
            <Price price={availableUsdsW} currency={"EUR"} hideSign />
          </span>
          <span className={styles["label-small"]}>USDSw</span>
          <div className={styles["button-row"]}>
            <Button
              className={styles.button}
              height="SMALL"
              kind="SOLID"
              shape="RECT"
              borderRadius="MEDIUM"
              color="ORANGE"
              weight="BOLD"
              fontSize={16}
              onClick={openWithdraw}
              disabled={availableUsdsW <= 0}
            >
              {t("Withdraw")}
            </Button>
            <Button
              className={styles.button}
              height="SMALL"
              kind="SOLID"
              shape="RECT"
              borderRadius="MEDIUM"
              color="ORANGE"
              weight="BOLD"
              fontSize={16}
              onClick={openConvert}
              disabled={availableUsdsW <= 0}
            >
              {t("Convert")}
            </Button>
          </div>
        </Panel>
      )}

      <Modal
        isOpen={isConvertOpen}
        close={closeConvert}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("Convert USDSw to USDS"),
          caption: t("Convert USDSw to USDS"),
          label: t("Convert USDSw to USDS"),
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: 0 }}>{t("Convert USDSw to USDS")}</h2>
          <p style={{ color: "#666", textAlign: "center", margin: "10px 0 20px" }}>
            {t("Available USDSw: {{available}}", { available: availableUsdsW })}
          </p>
          <form onSubmit={handleConvert}>
            <div style={{ margin: "0 0 18px" }}>
              <label style={{ display: "block", marginBottom: 4, color: "#666", fontSize: 13 }}>{t("Amount")}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={convertAmount}
                  onChange={(e) => setConvertAmount(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 4,
                    border: `1px solid ${convertError ? "#c0392b" : "#ccc"}`,
                    fontSize: 14,
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setConvertAmount(String(availableUsdsW))}
                  style={{
                    padding: "0 12px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  {t("Max")}
                </button>
              </div>
              {convertError && <p style={{ color: "#c0392b", fontSize: 12, margin: "4px 0 0" }}>{convertError}</p>}
            </div>

            <div style={{ textAlign: "center" }}>
              <Button
                height="MEDIUM"
                kind="SOLID"
                shape="RECT"
                borderRadius="MEDIUM"
                color="ORANGE"
                weight="BOLD"
                fontSize={16}
                disabled={converting || availableUsdsW <= 0}
              >
                {converting ? t("Converting...") : t("Convert")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        isOpen={isWithdrawOpen}
        close={closeWithdraw}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("Withdraw USDSw"),
          caption: t("Withdraw USDSw"),
          label: t("Withdraw USDSw"),
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: 0 }}>{t("Withdraw USDSw")}</h2>
          <p style={{ color: "#666", textAlign: "center", margin: "10px 0 20px" }}>
            {t("Available USDSw: {{available}}", { available: availableUsdsW })}
          </p>
          <form onSubmit={handleWithdraw}>
            <div style={{ margin: "0 0 18px" }}>
              <label style={{ display: "block", marginBottom: 4, color: "#666", fontSize: 13 }}>{t("Amount")}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 4,
                    border: `1px solid ${withdrawError ? "#c0392b" : "#ccc"}`,
                    fontSize: 14,
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setWithdrawAmount(String(availableUsdsW))}
                  style={{
                    padding: "0 12px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  {t("Max")}
                </button>
              </div>
            </div>

            <div style={{ margin: "0 0 18px" }}>
              <label style={{ display: "block", marginBottom: 4, color: "#666", fontSize: 13 }}>
                {t("Wallet address")}
              </label>
              <input
                type="text"
                value={withdrawWallet}
                onChange={(e) => setWithdrawWallet(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 4,
                  border: `1px solid ${withdrawError ? "#c0392b" : "#ccc"}`,
                  fontSize: 14,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            {withdrawError && <p style={{ color: "#c0392b", fontSize: 12, margin: "0 0 12px" }}>{withdrawError}</p>}

            <div style={{ textAlign: "center" }}>
              <Button
                height="MEDIUM"
                kind="SOLID"
                shape="RECT"
                borderRadius="MEDIUM"
                color="ORANGE"
                weight="BOLD"
                fontSize={16}
                disabled={withdrawing || availableUsdsW <= 0}
              >
                {withdrawing ? t("Requesting...") : t("Withdraw")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <div className={styles["tip-wrap"]}>
        <Heading className={styles["tip-title"]} level={6}>
          <span dangerouslySetInnerHTML={{ __html: t(`<strong>TIP</strong> about payment methods`) }} />:
        </Heading>
        <p className={styles.tip}>
          {t(
            "When making a payment, the system first deducts from your SCR balance, using as much as available but not exceeding the maximum allowable amount per product. Any remaining amount is then covered using your USDS balance.",
          )}
        </p>
      </div>
      <Panel className={styles.panel} contentClassName={styles.content} panelStyle="TERTIARY">
        <IconLogoSuccessFactoryNoText className={styles.logo} width={41} height={50} />
        <span className={styles.credit}>
          {scrInfo ? (
            <Price price={scrInfo.available} currency={scrInfo.currency.code} hideSign />
          ) : (
            // empty placeholder to prevent content 'jumping' when data hasn't loaded
            <div style={{ height: "37px" }} />
          )}
        </span>
        <p className={styles.label}>{t("SCR")}</p>
        <div className={styles.button} />
      </Panel>
    </Panel>
  );
};

function getShopCreditInfo(sources?: PaymentMethodsSource[] | null) {
  if (!sources) {
    return null;
  }

  const topUpSource = sources.filter((e) => e.info && e.info.__typename === "PaymentSourceShopCredit")[0];

  return topUpSource.info as PaymentSourceShopCredit;
}
function getScrCreditInfo(sources?: PaymentMethodsSource[] | null) {
  if (!sources) {
    return null;
  }

  const topUpSource = sources.filter((e) => e.info && e.info.__typename === "PaymentSourceScrCredit")[0];

  return topUpSource.info as PaymentSourceScrCredit;
}
